/**
 * Authentication store using Zustand
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { api } from '@/lib/api'

interface User {
    id: string
    tenantId: string
    email: string
    name: string
    role: string
    avatarUrl: string | null
    emailVerified: boolean
    createdAt: number
}

interface Tenant {
    id: string
    name: string
    slug: string
    plan: string
    logoUrl: string | null
}

interface AuthState {
    user: User | null
    tenant: Tenant | null
    accessToken: string | null
    refreshToken: string | null
    expiresAt: number | null
    isAuthenticated: boolean
    isLoading: boolean

    // Actions
    login: (email: string, password: string) => Promise<void>
    register: (data: { email: string; password: string; name: string; tenantName: string }) => Promise<void>
    logout: () => Promise<void>
    setTokens: (accessToken: string, refreshToken: string, expiresAt: number) => void
    refreshTokens: () => Promise<boolean>
    fetchUser: () => Promise<void>
    initialize: () => Promise<void>
}

export const useAuthStore = create<AuthState>()(
    persist(
        (set, get) => ({
            user: null,
            tenant: null,
            accessToken: null,
            refreshToken: null,
            expiresAt: null,
            isAuthenticated: false,
            isLoading: true,

            login: async (email, password) => {
                const response = await api.post<{ user: User; tenant: Tenant; accessToken: string; refreshToken: string; expiresAt: number }>('/auth/login', { email, password })
                const { user, tenant, accessToken, refreshToken, expiresAt } = response.data!

                set({
                    user,
                    tenant,
                    accessToken,
                    refreshToken,
                    expiresAt,
                    isAuthenticated: true,
                })
            },

            register: async (data) => {
                const response = await api.post<{ user: User; tenant: Tenant; accessToken: string; refreshToken: string; expiresAt: number }>('/auth/register', data)
                const { user, tenant, accessToken, refreshToken, expiresAt } = response.data!

                set({
                    user,
                    tenant,
                    accessToken,
                    refreshToken,
                    expiresAt,
                    isAuthenticated: true,
                })
            },

            logout: async () => {
                // Clear local state immediately (optimistic logout)
                // This ensures the UI updates/redirects immediately, even if the backend call takes time or fails
                set({
                    user: null,
                    tenant: null,
                    accessToken: null,
                    refreshToken: null,
                    expiresAt: null,
                    isAuthenticated: false,
                })

                try {
                    await api.post('/auth/logout')
                } catch {
                    // Ignore logout errors
                }
            },

            setTokens: (accessToken, refreshToken, expiresAt) => {
                set({ accessToken, refreshToken, expiresAt, isAuthenticated: true })
            },

            refreshTokens: async () => {
                const { refreshToken } = get()
                if (!refreshToken) return false

                try {
                    const response = await api.post<{ accessToken: string; expiresAt: number }>('/auth/refresh', { refreshToken })
                    const { accessToken, expiresAt } = response.data!

                    set({ accessToken, expiresAt })
                    return true
                } catch {
                    // Refresh failed, logout
                    set({
                        user: null,
                        tenant: null,
                        accessToken: null,
                        refreshToken: null,
                        expiresAt: null,
                        isAuthenticated: false,
                    })
                    return false
                }
            },

            fetchUser: async () => {
                try {
                    const response = await api.get<{ user: User; tenant: Tenant }>('/auth/me')
                    const { user, tenant } = response.data!
                    set({ user, tenant })
                } catch {
                    set({
                        user: null,
                        tenant: null,
                        accessToken: null,
                        refreshToken: null,
                        expiresAt: null,
                        isAuthenticated: false,
                    })
                }
            },

            initialize: async () => {
                const { accessToken, expiresAt, refreshTokens, fetchUser } = get()

                if (!accessToken) {
                    set({ isLoading: false })
                    return
                }

                // Check if token is expired
                if (expiresAt && expiresAt < Date.now()) {
                    const success = await refreshTokens()
                    if (!success) {
                        set({ isLoading: false })
                        return
                    }
                }

                // Fetch current user
                await fetchUser()
                set({ isLoading: false })
            },
        }),
        {
            name: 'keyframes-auth',
            partialize: (state) => ({
                accessToken: state.accessToken,
                refreshToken: state.refreshToken,
                expiresAt: state.expiresAt,
                isAuthenticated: state.isAuthenticated,
                user: state.user,
                tenant: state.tenant,
            }),
        }
    )
)

// Initialize auth on load
if (typeof window !== 'undefined') {
    useAuthStore.getState().initialize()
}
